/**
 * 需求来源：docs/旅游落地页/AI提示词.md 第1次提交 需求4（任务单 DEV-120352）；
 *          视觉基准 docs/旅游落地页/cj商旅-01.html 第 9617~9772 行（.cjbt-city-five）与第 9773~11395 行内联脚本。
 * 注意事项：
 * 1. 原型此区块的 tab / 城市信息 / 图库 slide / dots 全部由 JS innerHTML 动态拼装（首屏是空壳）；
 *    这里改为 **React 直出**，SSR 首屏即含第 1 座城市的完整内容（利于 SEO 抓取与首屏体验），
 *    但交互行为、类名、data-* 属性、无障碍属性与原型逐项对齐。
 * 2. 逐项复刻的交互：
 *    - 城市切换 210ms 过渡（is-switching + aria-busy），切换后 slideIndex 归零、tab 滚动居中（ensureTabVisible）；
 *    - tab 键盘导航 Arrow/Home/End，切换后 240ms 聚焦目标 tab（等过渡结束，与原型一致）；
 *    - 图库 cardsPerView 按视口分档：≤767→1、≤1080→3、其余→5；maxSlideIndex = 图片数 − cardsPerView；
 *    - 位移取目标 slide 的 offsetLeft（非等宽假设），animate=false 时临时关掉 transition 再于 rAF 内恢复；
 *    - 指针拖拽：位移 >6px 记 ignoreClick 抑制误触，松手阈值 ±55px 翻页；
 *    - resize 130ms 防抖重算分档。
 * 3. 「Add {city} to My Journey」按钮文案随城市变化，走 i18n 插值（constants 的 addToJourney 函数）。
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ArrowRight, ArrowLeft, IconDrag } from './Icons';

/** 两位补零（原型 padNumber）。 */
function padNumber(value) {
  var s = String(value);
  return s.length < 2 ? '0' + s : s;
}

/** 视口分档：与原型 getCardsPerView 完全一致。 */
function getCardsPerView() {
  if (typeof window === 'undefined') return 5;
  var width = window.innerWidth;
  if (width <= 767) return 1;
  if (width <= 1080) return 3;
  return 5;
}

export default function CityExplorer(props) {
  var CX = props.data;
  var cities = CX.list;

  var [cityIndex, setCityIndex] = useState(0);
  var [slideIndex, setSlideIndex] = useState(0);
  var [switching, setSwitching] = useState(false);
  // SSR 与原型初值一致（原型 state.cardsPerView 初始为 5），挂载后按真实视口修正
  var [cardsPerView, setCardsPerView] = useState(5);

  var navWindowRef = useRef(null);
  var tabRefs = useRef([]);
  var trackRef = useRef(null);
  var viewportRef = useRef(null);
  var switchTimerRef = useRef(null);
  var focusTimerRef = useRef(null);
  var resizeTimerRef = useRef(null);
  var dragRef = useRef({ dragging: false, startX: 0, delta: 0, baseOffset: 0, ignoreClick: false });

  var city = cities[cityIndex];
  var maxSlideIndex = Math.max(0, city.images.length - cardsPerView);
  var dotCount = maxSlideIndex + 1;

  /** 取目标 slide 的 offsetLeft（原型 getSlideOffset）。 */
  var getSlideOffset = useCallback(function (index) {
    var track = trackRef.current;
    if (!track) return 0;
    var slides = track.querySelectorAll('[data-city-five-slide]');
    if (!slides.length || !slides[index]) return 0;
    return slides[index].offsetLeft;
  }, []);

  /** 应用轨道位移（原型 updateSlider 的 transform 部分；类名与计数由 React 派生）。 */
  var applyTransform = useCallback(function (index, animate) {
    var track = trackRef.current;
    if (!track) return;
    if (!animate) {
      track.style.transition = 'none';
    } else {
      track.style.removeProperty('transition');
    }
    track.style.transform = 'translate3d(' + (-getSlideOffset(index)) + 'px, 0, 0)';
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(function () {
        if (!animate) track.style.removeProperty('transition');
      });
    }
  }, [getSlideOffset]);

  /** tab 横向滚动居中（原型 ensureTabVisible）。 */
  var ensureTabVisible = useCallback(function (index) {
    var navWindow = navWindowRef.current;
    var button = tabRefs.current[index];
    if (!navWindow || !button) return;
    var targetLeft = button.offsetLeft - (navWindow.clientWidth - button.offsetWidth) / 2;
    var maximumScroll = navWindow.scrollWidth - navWindow.clientWidth;
    navWindow.scrollTo({
      left: Math.max(0, Math.min(targetLeft, maximumScroll)),
      behavior: 'smooth',
    });
  }, []);

  // 挂载后按真实视口修正分档（SSR 默认 5）
  useEffect(function () {
    setCardsPerView(getCardsPerView());
  }, []);

  // 城市/分档变化：位移瞬时归位（不带动画，等同原型 buildSlides → updateSlider(false)）
  useEffect(function () {
    applyTransform(slideIndex, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityIndex, cardsPerView]);

  // slideIndex 越界收敛（分档变大时 maxSlideIndex 变小）
  useEffect(function () {
    if (slideIndex > maxSlideIndex) setSlideIndex(maxSlideIndex);
  }, [slideIndex, maxSlideIndex]);

  // resize 130ms 防抖重算分档（原型同款）
  useEffect(function () {
    if (typeof window === 'undefined') return undefined;
    function onResize() {
      window.clearTimeout(resizeTimerRef.current);
      resizeTimerRef.current = window.setTimeout(function () {
        setCardsPerView(getCardsPerView());
      }, 130);
    }
    window.addEventListener('resize', onResize);
    return function () {
      window.clearTimeout(resizeTimerRef.current);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(function () {
    return function () {
      if (typeof window === 'undefined') return;
      window.clearTimeout(switchTimerRef.current);
      window.clearTimeout(focusTimerRef.current);
    };
  }, []);

  /** 切换城市：210ms 淡出淡入（原型 changeCity）。 */
  var changeCity = useCallback(function (index) {
    if (switching || index === cityIndex || !cities[index]) return;
    setSwitching(true);
    switchTimerRef.current = window.setTimeout(function () {
      setCityIndex(index);
      setSlideIndex(0);
      ensureTabVisible(index);
      window.requestAnimationFrame(function () { setSwitching(false); });
    }, 210);
  }, [switching, cityIndex, cities, ensureTabVisible]);

  var goToSlide = useCallback(function (index) {
    var next = Math.max(0, Math.min(index, maxSlideIndex));
    setSlideIndex(next);
    applyTransform(next, true);
  }, [maxSlideIndex, applyTransform]);

  var handleTabKeyDown = useCallback(function (event, currentIndex) {
    var nextIndex = currentIndex;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % cities.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + cities.length) % cities.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = cities.length - 1;
    } else {
      return;
    }
    event.preventDefault();
    changeCity(nextIndex);
    // 等 210ms 切换过渡结束再聚焦，避免过渡中焦点跳动（原型 240ms）
    focusTimerRef.current = window.setTimeout(function () {
      var node = tabRefs.current[nextIndex];
      if (node && node.focus) node.focus({ preventScroll: true });
    }, 240);
  }, [cities.length, changeCity]);

  // ---------------- 指针拖拽（原型 pointerdown/move/up + finishDrag） ----------------
  var onPointerDown = useCallback(function (event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    var drag = dragRef.current;
    drag.dragging = true;
    drag.ignoreClick = false;
    drag.startX = event.clientX;
    drag.delta = 0;
    drag.baseOffset = getSlideOffset(slideIndex);
    if (viewportRef.current) {
      viewportRef.current.classList.add('is-dragging');
      try { viewportRef.current.setPointerCapture(event.pointerId); } catch (e) { /* ignore */ }
    }
  }, [slideIndex, getSlideOffset]);

  var onPointerMove = useCallback(function (event) {
    var drag = dragRef.current;
    if (!drag.dragging) return;
    drag.delta = event.clientX - drag.startX;
    if (Math.abs(drag.delta) > 6) drag.ignoreClick = true;
    if (trackRef.current) {
      trackRef.current.style.transform = 'translate3d(' + (-drag.baseOffset + drag.delta) + 'px, 0, 0)';
    }
  }, []);

  var finishDrag = useCallback(function (event) {
    var drag = dragRef.current;
    if (!drag.dragging) return;
    drag.dragging = false;
    if (viewportRef.current) {
      viewportRef.current.classList.remove('is-dragging');
      try { viewportRef.current.releasePointerCapture(event.pointerId); } catch (e) { /* Pointer already released */ }
    }
    var next = slideIndex;
    if (drag.delta < -55) next += 1;
    else if (drag.delta > 55) next -= 1;
    goToSlide(next);
    window.setTimeout(function () { drag.ignoreClick = false; }, 80);
  }, [slideIndex, goToSlide]);

  var onSlideClick = useCallback(function (index) {
    var drag = dragRef.current;
    if (drag.ignoreClick) {
      drag.ignoreClick = false;
      return;
    }
    goToSlide(Math.min(index, maxSlideIndex));
  }, [goToSlide, maxSlideIndex]);

  return (
    <section
      className={'cjbt-city-five' + (switching ? ' is-switching' : '')}
      id="china-city-explorer"
    >
      <div className="cjbt-city-five__container">

        {/* SECTION HEADING */}
        <div className="cjbt-city-five__heading">
          <div>
            <div className="cjbt-city-five__eyebrow">{CX.eyebrow}</div>
            <h2 className="cjbt-city-five__main-title">
              {CX.titleLead} <em>{CX.titleEm}</em>
            </h2>
          </div>
          <p className="cjbt-city-five__heading-text">{CX.headingText}</p>
        </div>

        {/* CITY NAVIGATION */}
        <div className="cjbt-city-five__nav-shell">
          <div className="cjbt-city-five__nav-window" data-city-five-nav-window ref={navWindowRef}>
            <div className="cjbt-city-five__nav" role="tablist" aria-label={CX.navLabel} data-city-five-nav>
              {cities.map(function (item, index) {
                var active = index === cityIndex;
                return (
                  <button
                    key={item.key}
                    className={'cjbt-city-five__tab' + (active ? ' is-active' : '')}
                    type="button"
                    role="tab"
                    aria-selected={active ? 'true' : 'false'}
                    tabIndex={active ? 0 : -1}
                    data-city-five-tab={index}
                    ref={function (node) { tabRefs.current[index] = node; }}
                    onClick={function () { changeCity(index); }}
                    onKeyDown={function (event) { handleTabKeyDown(event, index); }}
                  >
                    <span className="cjbt-city-five__tab-number">{padNumber(index + 1)}</span>
                    <span className="cjbt-city-five__tab-copy">
                      <strong>{item.name}</strong>
                      <span>{item.navText}</span>
                    </span>
                    <span className="cjbt-city-five__tab-arrow"><ArrowRight /></span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* CITY INFORMATION */}
        <article
          className="cjbt-city-five__panel"
          data-city-five-panel
          data-city-number={padNumber(cityIndex + 1)}
          aria-live="polite"
          aria-busy={switching ? 'true' : undefined}
        >
          <div className="cjbt-city-five__content" data-city-five-content>
            <span className="cjbt-city-five__city-label">
              {padNumber(cityIndex + 1)} · {CX.featuredLabel}
            </span>
            <h3 className="cjbt-city-five__city-title">{city.name}</h3>
            <span className="cjbt-city-five__city-subtitle">{city.subtitle}</span>
            <p className="cjbt-city-five__description">{city.description}</p>

            <div className="cjbt-city-five__tags">
              {city.tags.map(function (tag, index) {
                return <span className="cjbt-city-five__tag" key={index}>{tag}</span>;
              })}
            </div>

            <div className="cjbt-city-five__actions">
              <a
                className="cjbt-city-five__button cjbt-city-five__button--primary"
                href={CX.addToJourneyHref}
                target="_blank"
                rel="noopener noreferrer"
              >
                {CX.addToJourney(city.name)}
                <ArrowRight />
              </a>
            </div>
          </div>

          <div className="cjbt-city-five__highlights" data-city-five-highlights>
            {city.highlights.map(function (highlight, index) {
              return (
                <article className="cjbt-city-five__highlight" key={index}>
                  <span className="cjbt-city-five__highlight-number">{padNumber(index + 1)}</span>
                  <div>
                    <strong>{highlight.title}</strong>
                    <p>{highlight.text}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </article>

        {/* IMAGE GALLERY */}
        <div className="cjbt-city-five__gallery-wrap">

          <div className="cjbt-city-five__gallery-header">
            <div className="cjbt-city-five__gallery-title">
              <small data-city-five-gallery-label>{city.name} {CX.gallerySuffix}</small>
              <h3>{CX.galleryTitle}</h3>
            </div>

            <div className="cjbt-city-five__gallery-actions">
              <button
                className="cjbt-city-five__arrow"
                type="button"
                aria-label={CX.prevLabel}
                data-city-five-prev
                disabled={slideIndex <= 0}
                onClick={function () { goToSlide(slideIndex - 1); }}
              >
                <ArrowLeft />
              </button>

              <div className="cjbt-city-five__gallery-count">
                <strong data-city-five-current>{padNumber(slideIndex + 1)}</strong>
                <span data-city-five-total>/ {padNumber(dotCount)}</span>
              </div>

              <button
                className="cjbt-city-five__arrow"
                type="button"
                aria-label={CX.nextLabel}
                data-city-five-next
                disabled={slideIndex >= maxSlideIndex}
                onClick={function () { goToSlide(slideIndex + 1); }}
              >
                <ArrowRight />
              </button>
            </div>
          </div>

          <div
            className="cjbt-city-five__viewport"
            data-city-five-viewport
            ref={viewportRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={finishDrag}
            onPointerCancel={finishDrag}
          >
            <div className="cjbt-city-five__track" data-city-five-track ref={trackRef}>
              {city.images.map(function (image, index) {
                return (
                  <button
                    key={city.key + '-' + index}
                    className={'cjbt-city-five__slide' + (index === slideIndex ? ' is-current' : '')}
                    type="button"
                    data-city-five-slide={index}
                    aria-label={image.title}
                    onClick={function () { onSlideClick(index); }}
                  >
                    <img src={image.src} alt={image.title} loading="lazy" decoding="async" />
                    <span className="cjbt-city-five__slide-content">
                      <strong>{image.title}</strong>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="cjbt-city-five__dots" data-city-five-dots>
            {Array.apply(null, { length: dotCount }).map(function (_, index) {
              return (
                <button
                  key={index}
                  className={'cjbt-city-five__dot' + (index === slideIndex ? ' is-active' : '')}
                  type="button"
                  data-city-five-dot={index}
                  aria-label={CX.dotLabel(index + 1)}
                  onClick={function () { goToSlide(index); }}
                ></button>
              );
            })}
          </div>

          <div className="cjbt-city-five__interaction-note">
            <IconDrag />
            {CX.interactionNote}
          </div>

        </div>

      </div>
    </section>
  );
}
